import React, { useEffect, useState } from 'react';
import { Button, Tooltip } from 'antd';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    LogoutOutlined,
    GlobalOutlined,
    DownOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { removeAuthToken } from '../utils/storageUtil';
import {
    navSections,
    isGroup,
    activeLeafFor,
    groupKeyForLeaf,
    NavLeaf,
    BadgeSource,
} from '../navConfig';
import { useNavCounts } from '../hooks/useNavCounts';

interface AppShellProps {
    /** Heading shown in the top bar. */
    title: string;
    /** Optional sub-heading under the title. */
    subtitle?: string;
    /** Buttons rendered on the right of the top bar. */
    actions?: React.ReactNode;
    children: React.ReactNode;
}

/** Red count pill. Renders nothing at zero so a quiet queue stays quiet. */
const CountBadge: React.FC<{ value: number; compact?: boolean }> = ({ value, compact }) => {
    if (!value) return null;
    if (compact) {
        return (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-sidebar" />
        );
    }
    return (
        <span
            className="ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full
                       bg-rose-500/90 px-1.5 py-0.5 text-[11px] font-semibold leading-none
                       text-white shadow-sm"
        >
            {value > 99 ? '99+' : value}
        </span>
    );
};

const AppShell: React.FC<AppShellProps> = ({ title, subtitle, actions, children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const counts = useNavCounts();

    const activeLeaf = activeLeafFor(location.pathname);
    const activeGroup = groupKeyForLeaf(activeLeaf);

    const [collapsed, setCollapsed] = useState(false);
    const [openGroups, setOpenGroups] = useState<string[]>(activeGroup ? [activeGroup] : []);

    // Keep the group holding the current route expanded as the route changes.
    useEffect(() => {
        if (activeGroup && !openGroups.includes(activeGroup)) {
            setOpenGroups((prev) => [...prev, activeGroup]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeGroup]);

    const toggleGroup = (key: string) =>
        setOpenGroups((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );

    const badgeValue = (source?: BadgeSource) =>
        source ? counts[source] ?? 0 : 0;

    const logout = () => {
        removeAuthToken();
        navigate('/login');
    };

    const rowBase =
        'group relative flex h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm ' +
        'transition-all duration-200 ease-smooth focus-visible:outline focus-visible:outline-2 ' +
        'focus-visible:outline-offset-2 focus-visible:outline-brand';

    /** A top-level row: either a leaf link or a group toggle. */
    const renderLeaf = (leaf: NavLeaf, nested: boolean) => {
        const active = activeLeaf === leaf.key;
        const value = badgeValue(leaf.badge);

        if (nested) {
            return (
                <button
                    key={leaf.key}
                    onClick={() => navigate(leaf.path)}
                    className={[
                        'relative flex h-9 w-full items-center rounded-md py-0 pl-6 pr-3 text-left text-[13px]',
                        'transition-all duration-200 ease-smooth',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
                        'focus-visible:outline-brand',
                        active
                            ? 'bg-white/10 font-medium text-white'
                            : 'text-slate-400 hover:bg-white/5 hover:text-slate-100',
                    ].join(' ')}
                >
                    {/* Marker on the rail. */}
                    <span
                        className={[
                            'absolute left-[7px] h-1.5 w-1.5 rounded-full transition-all duration-200',
                            active ? 'scale-100 bg-brand' : 'scale-0 bg-slate-500',
                        ].join(' ')}
                    />
                    <span className="truncate">{leaf.label}</span>
                </button>
            );
        }

        return (
            <button
                key={leaf.key}
                onClick={() => navigate(leaf.path)}
                title={collapsed ? leaf.label : undefined}
                className={[
                    rowBase,
                    active
                        ? 'bg-brand text-white shadow-glow'
                        : 'text-slate-300 hover:bg-sidebar-hover hover:text-white',
                ].join(' ')}
            >
                <span
                    className={[
                        'absolute left-0 top-1/2 -translate-y-1/2 rounded-r-full bg-white',
                        'transition-all duration-300 ease-smooth',
                        active ? 'h-5 w-[3px] opacity-90' : 'h-0 w-[3px] opacity-0',
                    ].join(' ')}
                />
                <span
                    className={[
                        'shrink-0 text-base transition-transform duration-200',
                        active ? '' : 'group-hover:scale-110',
                    ].join(' ')}
                >
                    {leaf.icon}
                </span>
                {collapsed ? (
                    <CountBadge value={value} compact />
                ) : (
                    <>
                        <span className="truncate">{leaf.label}</span>
                        <CountBadge value={value} />
                    </>
                )}
            </button>
        );
    };

    return (
        <div className="flex min-h-screen bg-canvas">
            <aside
                className={[
                    'sticky top-0 flex h-screen shrink-0 flex-col bg-sidebar',
                    'transition-[width] duration-300 ease-smooth',
                    collapsed ? 'w-[76px]' : 'w-[232px]',
                ].join(' ')}
            >
                <div className="flex h-16 shrink-0 items-center gap-3 border-b border-white/10 px-5">
                    <div
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px]
                                   bg-brand text-[15px] font-bold text-white shadow-glow"
                    >
                        A
                    </div>
                    <span
                        className={[
                            'whitespace-nowrap text-[15px] font-semibold tracking-wide text-slate-50',
                            'transition-opacity duration-200',
                            collapsed ? 'pointer-events-none opacity-0' : 'opacity-100',
                        ].join(' ')}
                    >
                        Admin
                    </span>
                </div>

                <nav className="flex-1 overflow-y-auto overflow-x-hidden p-3">
                    {navSections.map((section) => (
                        <div key={section.heading} className="mb-4 last:mb-0">
                            <div
                                className={[
                                    'px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.12em]',
                                    'text-slate-500 transition-opacity duration-200',
                                    collapsed ? 'pointer-events-none h-0 overflow-hidden opacity-0' : 'opacity-100',
                                ].join(' ')}
                            >
                                {section.heading}
                            </div>

                            <div className="flex flex-col gap-1">
                                {section.items.map((item) => {
                                    if (!isGroup(item)) return renderLeaf(item, false);

                                    const open = openGroups.includes(item.key) && !collapsed;
                                    const holdsActive = item.children.some((c) => c.key === activeLeaf);

                                    return (
                                        <div key={item.key}>
                                            <button
                                                onClick={() =>
                                                    // Collapsed rail has no room to expand — go to the list instead.
                                                    collapsed
                                                        ? navigate(item.children[0].path)
                                                        : toggleGroup(item.key)
                                                }
                                                title={collapsed ? item.label : undefined}
                                                aria-expanded={open}
                                                className={[
                                                    rowBase,
                                                    holdsActive && !open
                                                        ? 'bg-white/10 text-white'
                                                        : 'text-slate-300 hover:bg-sidebar-hover hover:text-white',
                                                ].join(' ')}
                                            >
                                                <span
                                                    className={[
                                                        'shrink-0 text-base transition-transform duration-200',
                                                        'group-hover:scale-110',
                                                    ].join(' ')}
                                                >
                                                    {item.icon}
                                                </span>
                                                {!collapsed && (
                                                    <>
                                                        <span className="truncate">{item.label}</span>
                                                        <DownOutlined
                                                            className={[
                                                                'ml-auto text-[10px] transition-transform duration-300 ease-smooth',
                                                                open ? 'rotate-180' : 'rotate-0',
                                                            ].join(' ')}
                                                        />
                                                    </>
                                                )}
                                            </button>

                                            {/* grid-rows 0fr -> 1fr animates to content height without guessing it. */}
                                            <div
                                                className={[
                                                    'grid transition-all duration-300 ease-smooth',
                                                    open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                                                ].join(' ')}
                                            >
                                                <div className="overflow-hidden">
                                                    <div className="relative ml-[22px] mt-1 flex flex-col gap-0.5
                                                                    border-l border-white/10 pl-1">
                                                        {item.children.map((child) => renderLeaf(child, true))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="shrink-0 border-t border-white/10 p-3">
                    <button
                        onClick={() => window.open(process.env.REACT_APP_WEBSITE_URL)}
                        title={collapsed ? '打开网站' : undefined}
                        className={`${rowBase} h-10 text-slate-400 hover:bg-sidebar-hover hover:text-white`}
                    >
                        <span className="shrink-0 text-base transition-transform duration-200 group-hover:scale-110">
                            <GlobalOutlined />
                        </span>
                        {!collapsed && <span className="truncate text-[13px]">打开网站</span>}
                    </button>
                    <button
                        onClick={logout}
                        title={collapsed ? '登出' : undefined}
                        className={`${rowBase} h-10 text-slate-400 hover:bg-rose-500/15 hover:text-rose-300`}
                    >
                        <span className="shrink-0 text-base transition-transform duration-200 group-hover:scale-110">
                            <LogoutOutlined />
                        </span>
                        {!collapsed && <span className="truncate text-[13px]">登出</span>}
                    </button>
                </div>
            </aside>

            <div className="flex min-w-0 flex-1 flex-col">
                <header
                    className="sticky top-0 z-10 flex h-16 items-center justify-between gap-4
                               border-b border-line bg-white/85 px-6 backdrop-blur-md"
                >
                    <div className="flex min-w-0 items-center gap-3">
                        <Button
                            type="text"
                            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            onClick={() => setCollapsed(!collapsed)}
                        />
                        <div className="min-w-0">
                            <h1 className="m-0 truncate text-[17px] font-semibold leading-tight text-ink">
                                {title}
                            </h1>
                            {subtitle && (
                                <p className="m-0 truncate text-xs leading-tight text-muted">{subtitle}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                        {actions}
                        <Tooltip title="登出">
                            <Button type="text" icon={<LogoutOutlined />} onClick={logout} />
                        </Tooltip>
                    </div>
                </header>

                {/* Keyed on pathname so each route fades up on entry. */}
                <main key={location.pathname} className="animate-fade-up p-6">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default AppShell;
